import { typeUtils } from '@festina-life/core'
import * as HeroIcons16 from '@heroicons/react/16/solid'
import * as HeroIcons20 from '@heroicons/react/20/solid'
import * as HeroIcons24 from '@heroicons/react/24/solid'
import cx from 'classnames'
import type { RefObject } from 'react'

import {
  type PropsWithSafeExtra,
  safeRestProps,
} from '../../libs/safeRestProps'
import { typographyStyles } from '../typography/styles'
import * as CustomIcons from './custom'
import * as Flags from './flags'
import { pascalCaseToSentence } from './pascalCaseToSentence'
import { isSemanticIcon, semanticIconMap } from './semanticIconMap'
import type { IconName } from './types'

type DisallowSizeNumberClassName<TClassName extends string> =
  TClassName extends
    | `${string}size-${number}${string}`
    | `${string}size-[${string}]${string}`
    ? 'Icon className cannot include size-* utilities. Use the size prop instead.' & {
        __iconClassNameError__: never
      }
    : TClassName

type Props<TClassName extends string = string> = PropsWithSafeExtra<
  'svg',
  'id',
  {
    /**
     * Options styles, is e.g. in combination with color: 'unset' to change the icon color
     */
    className?: DisallowSizeNumberClassName<TClassName>
    /**
     * Size of the icon
     * @defaultValue `'sm'`
     * @description 'sm' is 16px, 'md' is 20px and 'lg' is 24px
     * @see https://heroicons.com/ for reference of hero icon sizes
     */
    size?: 'sm' | 'md' | 'lg' | 'display'
    /**
     * Color of the icon. The default makes it grey; use 'unset' to make it inherit text color and allow for local overrides
     * @defaultValue `'default'`
     */
    color?: 'unset' | 'default'
    /**
     * Name of the icon - see storybook for all available icons
     * @see Storybook
     * @example 'AcademicCap' (hero icon), 'DanishFlag' (flag), 'FestinaLogo' (custom icon)
     */
    name: IconName
    /**
     * Title attribute for the icon - fallbacks to a human friendly version of the name if not provided
     */
    title?: string
    ref?: RefObject<SVGSVGElement>
  }
>

export const Icon = <TClassName extends string = string>({
  id,
  className,
  size = 'sm',
  color = 'default',
  name,
  title = pascalCaseToSentence(name),
  ref,
  extraProps = {},
  ...restProps // Needed since we can pass other props like aria-*, data-* etc. or spread that isn't type checked
}: Props<TClassName>) => {
  const baseStyles = cx(
    'shrink-0',
    {
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-6',
      display: 'size-20',
    }[size],
    className,
  )

  const baseProps = {
    id,
    ref,
    ...safeRestProps(extraProps, restProps),
    'aria-label': title,
    'aria-hidden': false, // needed to override default heroicon behavior which is aria-hidden=true
  }

  if (typeUtils.isKeyOfObject(name, Flags)) {
    const FlagIcon = Flags[name]
    return <FlagIcon {...baseProps} className={baseStyles} />
  }

  const HeroIconForThatSize = {
    sm: HeroIcons16,
    md: HeroIcons20,
    lg: HeroIcons24,
    display: HeroIcons24,
  }[size]

  const IconComponent = isSemanticIcon(name)
    ? semanticIconMap(name)
    : {
        ...HeroIconForThatSize,
        ...CustomIcons,
      }[`${name}Icon`]

  return (
    <IconComponent
      {...baseProps}
      className={cx(
        baseStyles,
        {
          unset: 'opacity-90', // deliberately less opaque to look nice in e.g. buttons
          default: cx('opacity-70', typographyStyles({ intensity: 'primary' })),
        }[color],
      )}
    />
  )
}
